import { Box, Center, Flex, Text } from '@mantine/core';
import { UserSwitchIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import type { ComponentProps, FC } from 'react';
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router';

import { useLoggedInUserQuery } from '#data/user/useLoggedInUserQuery.ts';

import { DevToolsPanel } from '../components/dev/DevToolsPanel.tsx';
import { BackToGogigi } from '../components/global/BackToGogigi/BackToGogigi.tsx';
import { Footer } from '../components/global/Footer/Footer.tsx';
import { Header } from '../components/global/Header/Header.tsx';
import { Logo } from '../components/global/Logo/Logo.tsx';
import { usePromoterItemQuery } from '../data/promoter/usePromoterItemQuery.ts';
import { useUserQuery } from '../data/user/useUserQuery.ts';
import { env } from '../env.ts';
import { useLogout } from '../hooks/data/auth.ts';
import { useI18n } from '../hooks/useI18n.ts';

// For now the menu items are empty
const menuItems: ComponentProps<typeof Header>['menuItems'] = [];

export const Root: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBackBar, setShowBackBar] = useState(false);

  const featureEnabled = env.VITE_FEATURE_GOGIGI_PROMOTER_LINKS === 'true' && !!env.VITE_FRONTEND_URL;

  useEffect(() => {
    if (!featureEnabled) return;

    if (searchParams.get('from') === 'gogigi') {
      sessionStorage.setItem('from-gogigi', 'true');
      searchParams.delete('from');
      setSearchParams(searchParams, { replace: true });
    }

    setShowBackBar(sessionStorage.getItem('from-gogigi') === 'true');
  }, [featureEnabled, searchParams, setSearchParams]);

  const { activeLocale, setActiveLocale, availableLanguages } = useI18n();
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();

  const { data: chosenUserData } = useUserQuery();
  const { data: loggedInUser } = useLoggedInUserQuery();
  const { data: promoterData } = usePromoterItemQuery();

  const onLogout = () => {
    logout(undefined, {
      onSuccess: () => {
        void navigate('/auth/login');
      },
    });
  };

  if (!availableLanguages || !activeLocale) {
    return (
      <Center h="100dvh" w="100dvw">
        <Logo />
      </Center>
    );
  }

  const isAdmin =
    loggedInUser != null && (loggedInUser.role as { admin_access?: boolean } | null)?.admin_access === true;
  const adminBannerHeight = 30;
  const backBarHeight = showBackBar ? 42 : 0;

  return (
    <Box
      bg="gray.1"
      pt={isAdmin ? 110 + adminBannerHeight + backBarHeight : 110 + backBarHeight}
      style={{ overflow: 'clip' }}
      w="100%"
    >
      {showBackBar && env.VITE_FRONTEND_URL && <BackToGogigi frontendUrl={env.VITE_FRONTEND_URL} />}
      {isAdmin && chosenUserData && (
        <Flex
          align="center"
          bg="blue.6"
          gap="xs"
          h={adminBannerHeight}
          justify="center"
          left={0}
          pos="fixed"
          px="md"
          right={0}
          style={{ zIndex: 200 }}
          top={backBarHeight}
        >
          <UserSwitchIcon color="white" size={14} weight="bold" />
          <Text c="white" fw={500} size="xs">
            Admin mode — viewing as {chosenUserData.first_name} {chosenUserData.last_name}
          </Text>
          <Link style={{ color: 'white', fontSize: 12, marginLeft: 8 }} to="/log-in-as">
            Switch
          </Link>
        </Flex>
      )}
      <Header
        activeLocale={activeLocale}
        adminBannerOffset={(isAdmin ? adminBannerHeight : 0) + backBarHeight}
        adminUser={
          loggedInUser != null && (loggedInUser.role as { admin_access?: boolean } | null)?.admin_access === true
            ? {
                name:
                  loggedInUser.first_name && loggedInUser.last_name
                    ? `${loggedInUser.first_name} ${loggedInUser.last_name}`
                    : null,
                email: loggedInUser.email,
              }
            : undefined
        }
        languages={availableLanguages}
        menuItems={menuItems}
        setActiveLocale={setActiveLocale}
        user={
          chosenUserData != null && promoterData != null
            ? {
                name:
                  chosenUserData.first_name && chosenUserData.last_name
                    ? `${chosenUserData.first_name} ${chosenUserData.last_name}`
                    : undefined,
                email: chosenUserData.email ?? undefined,
                promoterName: promoterData.name ?? undefined,
              }
            : undefined
        }
        onLogout={onLogout}
      />
      <Box pos="relative" style={{ minHeight: 'calc(100dvh - 358.25px)' }}>
        <Outlet />
      </Box>
      <Footer />
      <DevToolsPanel />
    </Box>
  );
};
